import {
  Box,
  Flex,
  HStack,
  Stack,
  Text,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Grid,
  Icon,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RiRobotLine,
  RiFolder3Line,
  RiNotification3Line,
  RiSparklingLine,
  RiArrowRightLine,
  RiVipCrownLine,
  RiShieldCheckLine,
  RiTeamLine,
  RiAddLine,
  RiFlashlightLine,
} from 'react-icons/ri';
import { FiMail, FiCopy } from 'react-icons/fi';
import { GoPeople } from 'react-icons/go';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLoading } from '../HelivanLoading';
import { HelivanLabel } from '../helivan/HelivanLabel';
import { HelivanButton } from '../helivan/HelivanButton';
import { useGetUsageLimitsQuery } from '../../generated/graphql';

const ContactModal = ({ isOpen, onClose, title }: { isOpen: boolean; onClose: () => void; title: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText('info@helivan.io');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="12px">
        <ModalHeader pb={2}>
          <HelivanText variant="largeSubheader" colorOverride={HelivanColor.text.helivanBlue}>
            {title}
          </HelivanText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="center">
            <Flex
              bg={HelivanColor.text.blueLight}
              p={3}
              borderRadius="full"
            >
              <FiMail size={24} color={HelivanColor.text.helivanBlue} />
            </Flex>
            <VStack spacing={2}>
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                Contact us to purchase this add-on
              </HelivanText>
              <Flex
                bg={HelivanColor.background.secondary}
                px={4}
                py={2}
                borderRadius="8px"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                onClick={handleCopy}
                _hover={{ bg: HelivanColor.background.lightestGrey }}
                align="center"
                gap={2}
              >
                <HelivanText variant="largeBody" colorOverride={HelivanColor.text.helivanBlue}>
                  info@helivan.io
                </HelivanText>
                <Icon as={FiCopy} color={HelivanColor.text.blueMid} boxSize={4} />
              </Flex>
              <HelivanText variant="smallBody" colorOverride={copied ? 'green.500' : 'transparent'}>
                {copied ? 'Copied!' : '\u00A0'}
              </HelivanText>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const planConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'blue' | 'blueDark'; icon: any; color: string }> = {
  FREE: { label: 'Free', variant: 'blueDark', icon: RiShieldCheckLine, color: HelivanColor.text.blueMid },
  RESEARCH: { label: 'Research', variant: 'blue', icon: RiSparklingLine, color: HelivanColor.text.helivanBlue },
  PRO: { label: 'Pro', variant: 'success', icon: RiVipCrownLine, color: '#10b981' },
  TEAM: { label: 'Team', variant: 'warning', icon: RiTeamLine, color: '#f59e0b' },
};

const getProgressColor = (percent: number): string => {
  if (percent >= 100) return '#ef4444';
  if (percent >= 80) return '#f59e0b';
  return HelivanColor.text.helivanBlue;
};

interface UsageCardProps {
  icon: any;
  title: string;
  subtitle: string;
  current: number;
  max: number;
  unit?: string;
  showProgress?: boolean;
  warningMessage?: string;
}

const UsageCard = ({ icon, title, subtitle, current, max, unit = '', showProgress = true, warningMessage }: UsageCardProps) => {
  const percent = max > 0 ? (current / max) * 100 : 0;
  const progressColor = getProgressColor(percent);
  const isUnlimited = max >= 999999;

  return (
    <Flex
      direction="column"
      bg="white"
      borderRadius="10px"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      overflow="hidden"
      transition="all 0.15s ease"
      _hover={{
        borderColor: HelivanColor.text.helivanBlue,
      }}
    >
      {/* Card Content */}
      <Flex p="14px" alignItems="center" gap={3}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="8px"
          bg="rgba(0,71,125,0.08)"
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize="16px" color={HelivanColor.text.helivanBlue} />
        </Flex>

        <Box flex="1" minW={0}>
          <Text fontSize="13px" fontWeight={600} color={HelivanColor.text.blueDark} lineHeight="1.2">
            {title}
          </Text>
          <Text fontSize="11px" color={HelivanColor.text.blueMid} lineHeight="1.3" noOfLines={1}>
            {subtitle}
          </Text>
        </Box>

        {showProgress ? (
          <Flex alignItems="center" gap={3}>
            <CircularProgress
              value={Math.min(percent, 100)}
              color={progressColor}
              trackColor={HelivanColor.background.secondary}
              size="48px"
              thickness="10px"
              capIsRound
            >
              <CircularProgressLabel>
                <Text fontSize="11px" fontWeight={700} color={HelivanColor.text.blueDark}>
                  {Math.round(percent)}%
                </Text>
              </CircularProgressLabel>
            </CircularProgress>
            <Box textAlign="right">
              <Text fontSize="18px" fontWeight={700} color={HelivanColor.text.blueDark} lineHeight="1">
                {current}
                <Text as="span" fontSize="12px" fontWeight={500} color={HelivanColor.text.blueMid}>
                  /{isUnlimited ? '∞' : max}
                </Text>
              </Text>
              {warningMessage ? (
                <Text fontSize="10px" color="#ef4444" fontWeight={500}>
                  {warningMessage}
                </Text>
              ) : (
                <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                  {isUnlimited ? 'unlimited' : `${max - current} left`}
                </Text>
              )}
            </Box>
          </Flex>
        ) : (
          <Box textAlign="right">
            <Text fontSize="20px" fontWeight={700} color={HelivanColor.text.blueDark} lineHeight="1">
              {current}
              <Text as="span" fontSize="12px" fontWeight={500} color={HelivanColor.text.blueMid} ml={1}>
                {unit}
              </Text>
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

interface AddOnCardProps {
  icon: any;
  title: string;
  description: string;
  price: string;
  priceUnit: string;
  level: 'team' | 'project';
  onPurchase: () => void;
}

const AddOnCard = ({ icon, title, description, price, priceUnit, level, onPurchase }: AddOnCardProps) => (
  <Flex
    bg="white"
    borderRadius="10px"
    border="1px solid"
    borderColor={HelivanColor.border.primary}
    p="14px"
    alignItems="center"
    justifyContent="space-between"
    gap={3}
    _hover={{ borderColor: HelivanColor.text.helivanBlue }}
    transition="all 0.15s ease"
  >
    <HStack spacing={3} flex="1">
      <Flex
        w="36px"
        h="36px"
        borderRadius="8px"
        bg="rgba(0,71,125,0.08)"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon as={icon} boxSize="18px" color={HelivanColor.text.helivanBlue} />
      </Flex>
      <Box flex="1" minW={0}>
        <HStack spacing={2} mb="2px">
          <Text fontSize="13px" fontWeight={600} color={HelivanColor.text.blueDark}>
            {title}
          </Text>
          <HelivanLabel
            label={level === 'team' ? 'Team' : 'Project'}
            variant={level === 'team' ? 'warning' : 'blue'}
            size="sm"
          />
        </HStack>
        <Text fontSize="11px" color={HelivanColor.text.blueMid}>
          {description}
        </Text>
      </Box>
    </HStack>
    <HStack spacing={3}>
      <Box textAlign="right">
        <Text fontSize="18px" fontWeight={700} color={HelivanColor.text.helivanBlue} lineHeight="1">
          {price}
        </Text>
        <Text fontSize="10px" color={HelivanColor.text.blueMid}>
          {priceUnit}
        </Text>
      </Box>
      <HelivanButton variant="outline" size="xs" onClick={onPurchase}>
        <HStack spacing={1}>
          <RiAddLine size={12} />
          <Text fontSize="11px">Add</Text>
        </HStack>
      </HelivanButton>
    </HStack>
  </Flex>
);

export const UsageLimits = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useGetUsageLimitsQuery();
  const contactModal = useDisclosure();
  const [contactTitle, setContactTitle] = useState('Purchase Add-on');

  const handlePurchase = (title: string) => {
    setContactTitle(`Purchase: ${title}`);
    contactModal.onOpen();
  };

  if (loading) {
    return <HelivanLoading />;
  }

  if (error) {
    console.error('UsageLimits query error:', error);
    return (
      <Box p={4}>
        <HelivanText variant="smallBody" colorOverride="#ef4444">
          Failed to load usage limits: {error.message}
        </HelivanText>
      </Box>
    );
  }

  if (!data?.user?.usageLimits) {
    console.warn('UsageLimits: No data or user or usageLimits', { data });
    return (
      <Box p={4}>
        <HelivanText variant="smallBody" colorOverride="#ef4444">
          Unable to load usage limits. Please try refreshing the page.
        </HelivanText>
      </Box>
    );
  }

  const limits = data.user.usageLimits;
  const plan = limits.plan;
  const planInfo = planConfig[plan] || planConfig.FREE;

  return (
    <Stack spacing={4}>
      {/* Plan Header */}
      <Flex
        bg="white"
        borderRadius="10px"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        p="14px"
        alignItems="center"
        justifyContent="space-between"
      >
        <HStack spacing={3}>
          <Flex
            w="40px"
            h="40px"
            borderRadius="10px"
            bg={`${planInfo.color}15`}
            align="center"
            justify="center"
          >
            <Icon as={planInfo.icon} boxSize="20px" color={planInfo.color} />
          </Flex>
          <Box>
            <HStack spacing={2} mb="2px">
              <Text fontSize="15px" fontWeight={600} color={HelivanColor.text.blueDark}>
                Current Plan
              </Text>
              <HelivanLabel
                label={planInfo.label}
                variant={planInfo.variant}
                size="sm"
              />
            </HStack>
            <Text fontSize="12px" color={HelivanColor.text.blueMid}>
              {plan === 'FREE'
                ? 'Basic features included'
                : plan === 'RESEARCH'
                ? 'Advanced research features'
                : plan === 'PRO'
                ? 'Full professional access'
                : 'Enterprise team features'}
            </Text>
          </Box>
        </HStack>

        {plan === 'FREE' && (
          <Button
            size="sm"
            bg={HelivanColor.text.helivanBlue}
            color="white"
            _hover={{ bg: HelivanColor.hover.blueDarker }}
            leftIcon={<RiSparklingLine size={14} />}
            onClick={() => navigate('/pricing')}
            fontSize="13px"
            h="32px"
          >
            Upgrade
          </Button>
        )}
      </Flex>

      {/* Usage Cards */}
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={3}>
        <UsageCard
          icon={RiRobotLine}
          title="Custom Agents"
          subtitle="Agents you've created"
          current={limits.currentAgentCount}
          max={limits.maxAgents}
          showProgress
          warningMessage={!limits.canCreateAgent ? 'Limit reached' : undefined}
        />

        <UsageCard
          icon={RiFolder3Line}
          title="Projects"
          subtitle="Active monitoring projects"
          current={limits.currentProjectCount}
          max={limits.maxProjects}
          showProgress
          warningMessage={!limits.canCreateProject ? 'Limit reached' : undefined}
        />

        <UsageCard
          icon={RiNotification3Line}
          title="Changes Detected"
          subtitle="Across all monitored agents"
          current={limits.changesDetectedThisMonth}
          max={0}
          unit="this month"
          showProgress={false}
        />
      </Grid>

      {/* Upgrade CTA for Free users */}
      {plan === 'FREE' && (
        <Flex
          bg={HelivanColor.background.secondary}
          borderRadius="10px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          p="12px 16px"
          alignItems="center"
          justifyContent="space-between"
          gap={3}
        >
          <HStack spacing={3} flex="1">
            <RiSparklingLine size={18} color={HelivanColor.text.helivanBlue} />
            <Text fontSize="13px" color={HelivanColor.text.blueMid}>
              <Text as="span" fontWeight={600} color={HelivanColor.text.blueDark}>
                Need more?
              </Text>
              {' '}Upgrade to Research for up to 10 agents and 3 projects for just $9/mo
            </Text>
          </HStack>
          <Button
            size="xs"
            variant="outline"
            borderColor={HelivanColor.text.helivanBlue}
            color={HelivanColor.text.helivanBlue}
            _hover={{ bg: 'rgba(0,71,125,0.05)' }}
            rightIcon={<RiArrowRightLine size={12} />}
            onClick={() => navigate('/pricing')}
            fontSize="12px"
          >
            View Plans
          </Button>
        </Flex>
      )}

      {/* À La Carte Add-ons */}
      <Box>
        <HStack spacing={2} mb={3}>
          <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark}>
            À La Carte Add-ons
          </Text>
          <Text fontSize="12px" color={HelivanColor.text.blueMid}>
            Customize your plan with additional resources
          </Text>
        </HStack>
        <Stack spacing={2}>
          <AddOnCard
            icon={RiFolder3Line}
            title="Additional 3 Projects"
            description="Add 3 more projects with up to 50 agents each"
            price="$9"
            priceUnit="/mo"
            level="team"
            onPurchase={() => handlePurchase('Additional 3 Projects')}
          />
          <AddOnCard
            icon={GoPeople}
            title="Additional Seat"
            description="Add a team member to your organization (up to 15 total)"
            price="$29"
            priceUnit="/mo per seat"
            level="team"
            onPurchase={() => handlePurchase('Additional Seat')}
          />
          <AddOnCard
            icon={RiFlashlightLine}
            title="Faster Updates"
            description="More frequent monitoring for a specific project"
            price="$3"
            priceUnit="/project/mo"
            level="project"
            onPurchase={() => handlePurchase('Faster Updates')}
          />
        </Stack>
      </Box>

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModal.isOpen}
        onClose={contactModal.onClose}
        title={contactTitle}
      />
    </Stack>
  );
};
